import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, PaperPlaneTilt, MapPin, Storefront, Envelope, Phone, User, Globe, Tag, ImageSquare, CheckCircle, UploadSimple, X } from "@phosphor-icons/react";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
  "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
  "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
  "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","Washington DC","West Virginia","Wisconsin","Wyoming"
];

const CATEGORIES = [
  { value: "fair", label: "Fair / Market" },
  { value: "festival", label: "Festival" },
  { value: "community", label: "Community Event" },
  { value: "anime", label: "Anime / Comic Convention" },
  { value: "antique", label: "Antiques & Vintage" },
  { value: "art", label: "Art Show" },
  { value: "asian_cultural", label: "Asian Cultural" },
  { value: "bike", label: "Bike Show" },
  { value: "black_cultural", label: "Black Cultural" },
  { value: "car", label: "Car Show" },
  { value: "craft", label: "Craft Show" },
  { value: "food", label: "Food & Drink Festival" },
  { value: "health", label: "Health & Wellness" },
  { value: "holiday", label: "Holiday & Seasonal" },
  { value: "kids", label: "Kids & Family" },
  { value: "latino", label: "Latino / Hispanic" },
  { value: "lgbtq", label: "LGBTQ+ Pride" },
  { value: "music", label: "Music Festival" },
  { value: "pets", label: "Pets & Animals" },
  { value: "sports", label: "Sports & Outdoor" },
  { value: "state_fair", label: "State & County Fair" },
  { value: "tech", label: "Tech & Maker" },
  { value: "flea_market", label: "Flea Market" },
  { value: "farmers_market", label: "Farmers Market" },
  { value: "trade_show", label: "Trade Show / Expo" },
];

export default function SubmitEvent() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: "", city: "", state: "", start_date: "", end_date: "",
    description: "", address: "", category: "fair", website: "", time: "",
    booth_fee: "", booth_size: "", vendors_wanted: "", expected_attendance: "",
    contact_name: "", contact_email: "", contact_phone: "", organizer: "",
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    
    setImagePreview(URL.createObjectURL(file));
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImageUrl(res.data.image_url);
      toast.success("Image uploaded!");
    } catch (error) {
      console.error("Upload failed:", error.message);
      toast.error("Failed to upload image");
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.city || !form.state || !form.start_date || !form.end_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (imageUrl) payload.image_url = imageUrl;
      Object.keys(payload).forEach(k => { if (payload[k] === "") delete payload[k]; });
      const res = await axios.post(`${API}/events/submit`, payload, { withCredentials: true });
      setSubmittedName(form.name);
      setSubmitted(true);
      toast.success(res.data.message);
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to submit event";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Require sign-in
  if (!user) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="submit-event-login-prompt">
        <div className="flex flex-col items-center justify-center py-20 gap-5 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#E07A5F]/10 flex items-center justify-center">
            <PaperPlaneTilt weight="duotone" className="w-8 h-8 text-[#E07A5F]" />
          </div>
          <h2 className="text-2xl font-bold text-[#2B2D42]">Sign in to submit an event</h2>
          <p className="text-[#6C757D] max-w-md">
            Help vendors discover events we might have missed. Sign in with Google to submit fairs, festivals, and vendor events.
          </p>
          <Button onClick={login} className="bg-[#E07A5F] hover:bg-[#D46A50] text-white px-8 py-3 rounded-xl text-base">
            Sign in with Google
          </Button>
          <button onClick={() => navigate("/")} className="text-sm text-[#6C757D] hover:text-[#2B2D42] underline">
            Back to events
          </button>
        </div>
      </div>
    );
  }

  // Success page
  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="submit-event-success">
        <div className="flex flex-col items-center justify-center py-20 gap-5 text-center">
          <div className="w-20 h-20 rounded-full bg-[#81B29A]/10 flex items-center justify-center">
            <CheckCircle weight="duotone" className="w-12 h-12 text-[#81B29A]" />
          </div>
          <h2 className="text-2xl font-bold text-[#2B2D42]">Event Submitted!</h2>
          <p className="text-[#6C757D] max-w-md">
            <strong>{submittedName}</strong> has been submitted and is now under review. 
            You'll receive a confirmation email shortly. Once approved, it will appear on 411 Fest.
          </p>
          <div className="flex gap-3 mt-2">
            <Button onClick={() => { setSubmitted(false); setForm({ name: "", city: "", state: "", start_date: "", end_date: "", description: "", address: "", category: "fair", website: "", time: "", booth_fee: "", booth_size: "", vendors_wanted: "", expected_attendance: "", contact_name: "", contact_email: "", contact_phone: "", organizer: "" }); setImagePreview(null); setImageUrl(null); }} variant="outline" className="border-[#81B29A] text-[#81B29A]">
              Submit Another
            </Button>
            <Button onClick={() => navigate("/")} className="bg-[#E07A5F] hover:bg-[#D46A50] text-white">
              Back to Events
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="submit-event-page">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/")} className="p-2 rounded-lg hover:bg-[#E8E4DF] transition-colors" data-testid="back-to-home">
          <ArrowLeft weight="bold" className="w-5 h-5 text-[#2B2D42]" />
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#2B2D42]" data-testid="submit-event-title">Submit an Event</h1>
          <p className="text-sm text-[#6C757D]">Help vendors discover shows we might have missed</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Event Image */}
        <div className="bg-white rounded-xl border border-[#D6D3D1] p-4 sm:p-6">
          <h2 className="text-sm font-semibold text-[#6C757D] uppercase tracking-wider flex items-center gap-2 mb-4">
            <ImageSquare weight="duotone" className="w-4 h-4 text-[#E07A5F]" /> Event Photo / Flyer
          </h2>
          
          {imagePreview ? (
            <div className="relative rounded-lg overflow-hidden">
              <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover" />
              <button
                type="button"
                onClick={() => { setImagePreview(null); setImageUrl(null); if (fileRef.current) fileRef.current.value = ""; }}
                className="absolute top-2 right-2 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/80"
              >
                <X weight="bold" className="w-4 h-4" />
              </button>
              {uploading && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full h-40 border-2 border-dashed border-[#D6D3D1] rounded-lg flex flex-col items-center justify-center gap-2 hover:border-[#E07A5F] hover:bg-[#E07A5F]/5 transition-colors"
              data-testid="image-upload-btn"
            >
              <UploadSimple weight="duotone" className="w-8 h-8 text-[#A8A5A1]" />
              <span className="text-sm text-[#6C757D]">Click to upload event photo or flyer</span>
              <span className="text-xs text-[#A8A5A1]">JPG, PNG up to 5MB</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="image-file-input" />
        </div>

        {/* Event Details */}
        <div className="bg-white rounded-xl border border-[#D6D3D1] p-4 sm:p-6 space-y-4">
          <h2 className="text-sm font-semibold text-[#6C757D] uppercase tracking-wider flex items-center gap-2">
            <Tag weight="duotone" className="w-4 h-4 text-[#E07A5F]" /> Event Details
          </h2>

          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-1">Event Name *</label>
            <input type="text" required value={form.name} onChange={e => update("name", e.target.value)} placeholder="e.g., Cheatham County Fair" className="w-full px-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-event-name" />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-1">Description</label>
            <textarea value={form.description} onChange={e => update("description", e.target.value)} placeholder="Describe the event, what vendors can expect, etc." rows={3} className="w-full px-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none resize-none" data-testid="input-description" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">Category *</label>
              <select value={form.category} onChange={e => update("category", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none bg-white" data-testid="input-category">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">Event Website</label>
              <div className="relative">
                <Globe weight="duotone" className="absolute left-3 top-3 w-4 h-4 text-[#6C757D]" />
                <input type="url" value={form.website} onChange={e => update("website", e.target.value)} placeholder="https://..." className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-website" />
              </div>
            </div>
          </div>
        </div>

        {/* Location & Date */}
        <div className="bg-white rounded-xl border border-[#D6D3D1] p-4 sm:p-6 space-y-4">
          <h2 className="text-sm font-semibold text-[#6C757D] uppercase tracking-wider flex items-center gap-2">
            <MapPin weight="duotone" className="w-4 h-4 text-[#81B29A]" /> Location & Date
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">City *</label>
              <input type="text" required value={form.city} onChange={e => update("city", e.target.value)} placeholder="e.g., Ashland City" className="w-full px-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-city" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">State *</label>
              <select required value={form.state} onChange={e => update("state", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none bg-white" data-testid="input-state">
                <option value="">Select a state</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-1">Address / Venue</label>
            <input type="text" value={form.address} onChange={e => update("address", e.target.value)} placeholder="e.g., Cheatham County Fairgrounds, 870 Fairgrounds Road" className="w-full px-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-address" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">Start Date *</label>
              <input type="date" required value={form.start_date} onChange={e => update("start_date", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-start-date" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">End Date *</label>
              <input type="date" required value={form.end_date} onChange={e => update("end_date", e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-end-date" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">Hours</label>
              <input type="text" value={form.time} onChange={e => update("time", e.target.value)} placeholder="e.g., 10 AM - 8 PM" className="w-full px-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-time" />
            </div>
          </div>
        </div>

        {/* Vendor Info */}
        <div className="bg-white rounded-xl border border-[#D6D3D1] p-4 sm:p-6 space-y-4">
          <h2 className="text-sm font-semibold text-[#6C757D] uppercase tracking-wider flex items-center gap-2">
            <Storefront weight="duotone" className="w-4 h-4 text-[#3D405B]" /> Vendor Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">Booth Fee</label>
              <input type="text" value={form.booth_fee} onChange={e => update("booth_fee", e.target.value)} placeholder="e.g., $150 - $300" className="w-full px-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-booth-fee" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">Booth Size</label>
              <input type="text" value={form.booth_size} onChange={e => update("booth_size", e.target.value)} placeholder="e.g., 10x10" className="w-full px-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-booth-size" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">Vendor Types Wanted</label>
              <input type="text" value={form.vendors_wanted} onChange={e => update("vendors_wanted", e.target.value)} placeholder="e.g., Food, Crafts, Merchandise" className="w-full px-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-vendors-wanted" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">Expected Attendance</label>
              <input type="text" value={form.expected_attendance} onChange={e => update("expected_attendance", e.target.value)} placeholder="e.g., 10,000+" className="w-full px-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-attendance" />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-xl border border-[#D6D3D1] p-4 sm:p-6 space-y-4">
          <h2 className="text-sm font-semibold text-[#6C757D] uppercase tracking-wider flex items-center gap-2">
            <User weight="duotone" className="w-4 h-4 text-[#E07A5F]" /> Contact Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">Organizer / Organization</label>
              <input type="text" value={form.organizer} onChange={e => update("organizer", e.target.value)} placeholder="e.g., Cheatham County Fair Board" className="w-full px-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-organizer" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">Contact Name</label>
              <div className="relative">
                <User weight="duotone" className="absolute left-3 top-3 w-4 h-4 text-[#6C757D]" />
                <input type="text" value={form.contact_name} onChange={e => update("contact_name", e.target.value)} placeholder="Full name" className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-contact-name" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">Email</label>
              <div className="relative">
                <Envelope weight="duotone" className="absolute left-3 top-3 w-4 h-4 text-[#6C757D]" />
                <input type="email" value={form.contact_email} onChange={e => update("contact_email", e.target.value)} placeholder="contact@example.com" className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-contact-email" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-1">Phone</label>
              <div className="relative">
                <Phone weight="duotone" className="absolute left-3 top-3 w-4 h-4 text-[#6C757D]" />
                <input type="tel" value={form.contact_phone} onChange={e => update("contact_phone", e.target.value)} placeholder="(555) 123-4567" className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[#D6D3D1] text-sm focus:ring-2 focus:ring-[#E07A5F]/30 focus:border-[#E07A5F] outline-none" data-testid="input-contact-phone" />
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <Button type="submit" disabled={submitting || uploading} className="w-full h-12 bg-[#E07A5F] hover:bg-[#D46A50] text-white rounded-xl font-medium text-base transition-colors" data-testid="submit-event-btn">
          <PaperPlaneTilt weight="bold" className="w-5 h-5 mr-2" />
          {submitting ? "Submitting..." : "Submit Event for Review"}
        </Button>
        
        <p className="text-xs text-[#A8A5A1] text-center">
          Submitted events are reviewed before appearing on 411 Fest. You'll receive a confirmation email.
        </p>
      </form>
    </div>
  );
}
